import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';

const fmt = (d: string) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const ACTION_LABELS: Record<string, string> = {
  lease_ended:       '📄 Lease Ended',
  lease_created:     '📄 Lease Created',
  lease_renewed:     '🔄 Lease Renewed',
  tenant_created:    '👤 Tenant Added',
  tenant_updated:    '✏️ Tenant Updated',
  tenant_archived:   '📥 Tenant Archived',
  tenant_restored:   '📤 Tenant Restored',
  property_archived: '📦 Property Archived',
  property_restored: '↩ Property Restored',
  payment_recorded:  '💷 Payment Recorded',
  rent_marked_paid: '✅ Rent Marked Paid',
  rent_payment_updated: '💷 Rent Payment Updated',
  rent_partial_payment_updated: '🧮 Rent Balance Updated',
  certificate_added:     '📋 Certificate Added',
  certificate_updated:   '✏️ Certificate Updated',
  certificate_deleted:   '🗑️ Certificate Deleted',
  maintenance_created:   '🔧 Maintenance Reported',
  maintenance_updated:   '🔧 Maintenance Updated',
  maintenance_completed: '✅ Maintenance Completed',
  viewing_booked:        '📅 Viewing Booked',
  viewing_updated:       '✏️ Viewing Updated',
  viewing_cancelled:     '✖️ Viewing Cancelled',
  viewing_deleted:       '🗑️ Viewing Deleted',
  tenant_statement_emailed: '📧 Statement Emailed',
  inventory_added:       '📦 Inventory Item Added',
  inventory_updated:     '✏️ Inventory Item Updated',
  inventory_deleted:     '🗑️ Inventory Item Deleted',
  maintenance_deleted:   '🗑️ Maintenance Deleted',
  maintenance_archived:  '📦 Maintenance Archived',
  maintenance_restored:  '↩ Maintenance Restored',
  lease_updated:         '✏️ Lease Updated',
  lease_deleted:         '🗑️ Lease Deleted',
  lease_notice_given:    '⏰ Notice Given',
  lease_document_uploaded: '📁 Document Uploaded',
  lease_agreement_generated: '📄 Agreement Generated',
  lease_agreement_emailed: '📧 Agreement Emailed',
  property_created:      '🏠 Property Added',
  property_updated:      '✏️ Property Updated',
  property_deleted:      '🗑️ Property Deleted',
  property_photo_uploaded: '📷 Property Photo Added',
  property_photo_deleted:  '🗑️ Property Photo Removed',
  property_ready_to_rent:  '✅ Ready to Rent',
  property_unmarked_ready: '↩ Unmarked Ready',
  pipeline_created:      '🏗️ Pipeline Deal Added',
  pipeline_updated:      '✏️ Pipeline Updated',
  pipeline_stage_changed:'📊 Pipeline Stage Changed',
  pipeline_completed:    '✅ Pipeline Completed',
  pipeline_deleted:      '🗑️ Pipeline Deleted',
  lease_added_to_tenant: '📄 Lease Linked to Tenant',
  tenant_deleted:        '🗑️ Tenant Deleted',
  tenant_portal_invited: '📨 Portal Invite Sent',
  deposit_returned:      '💰 Deposit Returned',
  quick_let_onboard:     '⚡ Quick Let Onboarded',
  certificate_renewed:   '🔄 Certificate Renewed',
  lease_document_deleted:'🗑️ Lease Document Deleted',
  rent_overdue_chase:    '📧 Rent Overdue Chase',
  rent_receipt_emailed:  '📧 Rent Receipt Emailed',
  tenant_photo_uploaded: '📷 Tenant Photo Added',
  tenant_referencing_updated: '✅ Referencing Updated',
  room_created:          '🚪 Room Added',
  room_updated:          '✏️ Room Updated',
  room_deleted:          '🗑️ Room Deleted',
  section13_notice_served:'📋 Section 13 Notice',
  section13_rent_applied:'💷 Section 13 Applied',
};

const ACTION_COLORS: Record<string, string> = {
  lease_ended:       '#dc2626',
  lease_created:     '#16a34a',
  lease_renewed:     '#0d9488',
  tenant_created:    '#16a34a',
  tenant_updated:    '#2563eb',
  tenant_archived:   '#64748b',
  tenant_restored:   '#0d9488',
  property_archived: '#9ca3af',
  property_restored: '#0d9488',
  payment_recorded:  '#7c3aed',
  rent_marked_paid: '#16a34a',
  rent_payment_updated: '#7c3aed',
  rent_partial_payment_updated: '#d97706',
  certificate_added:     '#16a34a',
  certificate_updated:   '#2563eb',
  certificate_deleted:   '#dc2626',
  maintenance_created:   '#d97706',
  maintenance_updated:   '#2563eb',
  maintenance_completed: '#16a34a',
  viewing_booked:        '#0d9488',
  viewing_updated:       '#2563eb',
  viewing_deleted:       '#dc2626',
  viewing_cancelled:     '#c2410c',
  inventory_added:       '#16a34a',
  inventory_updated:     '#2563eb',
  inventory_deleted:     '#dc2626',
  maintenance_deleted:   '#dc2626',
  maintenance_archived:  '#64748b',
  maintenance_restored:  '#2563eb',
  lease_updated:         '#2563eb',
  lease_deleted:         '#dc2626',
  lease_notice_given:    '#d97706',
  lease_document_uploaded: '#0d9488',
  lease_agreement_generated: '#0d9488',
  lease_agreement_emailed: '#16a34a',
  property_created:      '#16a34a',
  property_updated:      '#2563eb',
  property_deleted:      '#dc2626',
  property_photo_uploaded: '#0d9488',
  property_photo_deleted:  '#64748b',
  property_ready_to_rent:  '#16a34a',
  property_unmarked_ready: '#64748b',
  pipeline_created:      '#16a34a',
  pipeline_updated:      '#2563eb',
  pipeline_stage_changed:'#7c3aed',
  pipeline_completed:    '#16a34a',
  pipeline_deleted:      '#dc2626',
  lease_added_to_tenant: '#0d9488',
  tenant_deleted:        '#dc2626',
  tenant_portal_invited: '#db2777',
  deposit_returned:      '#16a34a',
  quick_let_onboard:     '#d97706',
  certificate_renewed:   '#0d9488',
  lease_document_deleted:'#dc2626',
  rent_overdue_chase:    '#dc2626',
  rent_receipt_emailed:  '#2563eb',
  tenant_photo_uploaded: '#2563eb',
  tenant_referencing_updated: '#0d9488',
  room_created:          '#16a34a',
  room_updated:          '#2563eb',
  room_deleted:          '#dc2626',
  tenant_statement_emailed: '#2563eb',
  section13_notice_served:'#d97706',
  section13_rent_applied:'#7c3aed',
};

const fmtDetails = (details: Record<string, unknown>) => {
  const entries = Object.entries(details).filter(([, v]) => v != null && v !== '');
  const idKeys = ['tenant_id', 'lease_id', 'property_id', 'viewing_id', 'job_id', 'cert_id', 'pipeline_id', 'item_id', 'increase_id', 'doc_id', 'photo_id', 'room_id', 'rent_id'];
  const sorted = [
    ...entries.filter(([k]) => idKeys.includes(k)),
    ...entries.filter(([k]) => !idKeys.includes(k)),
  ];
  return sorted;
};

export default function LandlordAuditLog() {
  const { token } = useAuth();
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/landlord/audit-log`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { entries: [] })
      .then(d => { setEntries(d.entries || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const filtered = entries.filter(e => {
    const q = search.toLowerCase();
    return !q || (e.action || '').includes(q) || (e.tenant_name || '').toLowerCase().includes(q) || (e.property_address || '').toLowerCase().includes(q);
  });

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto', fontFamily: "'DM Sans', sans-serif" }}>
      {/* Banner */}
      <div style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)', borderRadius: 16, padding: '24px 28px', marginBottom: 24, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>📋 Audit Log</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94a3b8' }}>Full history of all account activity</p>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 700, color: '#cbd5e1' }}>
          {filtered.length} record{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Search */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '8px 14px', marginBottom: 20 }}>
        <span style={{ color: '#94a3b8' }}>🔍</span>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search action, tenant, or property…"
          style={{ border: 'none', outline: 'none', flex: 1, fontSize: 14, fontFamily: 'inherit', color: '#0f172a' }}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af', fontSize: 16 }}>Loading activity…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 12, border: '2px dashed #e5e7eb' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#374151' }}>{entries.length === 0 ? 'No activity recorded yet' : 'No matching entries'}</div>
          <div style={{ color: '#6b7280', marginTop: 6, fontSize: 13 }}>Activity will appear here as you use the system</div>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                {['Log #', 'Date & Time', 'Action', 'Tenant', 'Property', 'Details'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((e, i) => {
                const color = ACTION_COLORS[e.action] || '#64748b';
                const label = ACTION_LABELS[e.action] || e.action;
                const details = e.details ? (typeof e.details === 'string' ? JSON.parse(e.details) : e.details) : {};
                return (
                  <tr key={e.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: '#94a3b8', fontFamily: "'DM Mono', monospace" }}>#{e.id}</td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', fontFamily: "'DM Mono', monospace" }}>
                      {fmt(e.created_at)}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ background: color + '18', color, padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {label}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151', fontWeight: 600 }}>
                      {e.tenant_name || <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>
                      {e.property_address || <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: '#6b7280', maxWidth: 320 }}>
                      {Object.keys(details).length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {fmtDetails(details).map(([k, v]) => (
                            <span key={k} style={{
                              background: k.endsWith('_id') ? '#eff6ff' : '#f1f5f9',
                              padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                              color: k.endsWith('_id') ? '#1d4ed8' : '#374151',
                            }}>
                              {k.replace(/_/g, ' ')}: {String(v)}
                            </span>
                          ))}
                        </div>
                      ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
